/**
 * Contact Service
 *
 * Handles all contact CRUD operations. Uses useApiFetch for consistent
 * base URL, retries, and error handling. Used by the contact store.
 */

import type { Contact, ContactCreateBody, ContactPatchBody } from '~~/layers/base/types/contact'
import type {
    AddressPayload,
    FetchContactsOptions,
    FetchContactsResponse,
    GetContactResponse,
} from '~~/layers/base/stores/contact.store'
import { useApiFetch } from '@/composables/useApiFetch'
import type { RecentHandwritingProject } from '~~/apps/capacitor/types/recentHandwritingProject'

export const useContactService = () => {
    /**
     * Fetch a page of contacts (with all their addresses + pagination metadata) from
     * GET /api/contacts.
     * @param options – optional search, sort (default 'name'), page, pageSize, and
     *   searchExcludeAddresses (restricts `search` to Contact fields only, skipping Address
     *   fields - e.g. for a contact auto-complete picker where address matches aren't useful)
     * @returns Promise with { data: { contacts, pagination }, error, pending, refresh }
     */
    const doFetchContactList = async (options?: FetchContactsOptions) => {
        const sort = options?.sort ?? 'name'
        const search = options?.search?.trim() ?? ''
        const query: {
            sort: string
            search?: string
            page?: number
            pageSize?: number
            search_exclude_addresses?: boolean
        } = { sort }
        if (search.length > 0) query.search = search
        if (options?.page != null) query.page = options.page
        if (options?.pageSize != null) query.pageSize = options.pageSize
        if (options?.searchExcludeAddresses) query.search_exclude_addresses = true
        const result = await useApiFetch<FetchContactsResponse>('/api/contacts', {
            method: 'GET',
            query,
        })
        return result
    }

    /**
     * Create a contact.
     * @param body – name (required), optional address fields
     * @returns Promise with { data, error, pending, refresh }
     */
    const doCreateContact = async (body: ContactCreateBody) => {
        const result = await useApiFetch<Contact>('/api/contacts', {
            method: 'POST',
            body,
        })
        return result
    }

    /**
     * Create a contact with one address for the recipient drawer create flow.
     * Body: name + structured address payload. Returns created contact and address
     * rows so the caller can set the new recipient.
     */
    const doCreateContactWithAddress = async (body: {
        name: string
        phone?: string | null
        email?: string | null
        address: AddressPayload
    }) => {
        const result = await useApiFetch<{
            contact: { id: string }
            address: { id: string } | null
        }>('/api/contacts', {
            method: 'POST',
            body,
        })
        return result
    }

    /**
     * Fetch one contact by id with all addresses (list UIs may show many; edit drawer loads one row).
     * @param id – contact UUID
     * @returns Promise with { data: { contact, addresses }, error, pending, refresh }
     */
    const doFetchContactById = async (id: string) => {
        const result = await useApiFetch<GetContactResponse>(`/api/contacts/${id}`, {
            method: 'GET',
        })
        return result
    }

    /**
     * Update contact name only (for edit form; use address-level endpoints for addresses).
     * @param id – contact UUID
     * @param name – new name
     */
    const doPatchContactName = async (id: string, name: string) => {
        const result = await useApiFetch<Contact>(`/api/contacts/${id}`, {
            method: 'PATCH',
            body: { name },
        })
        return result
    }

    /**
     * Update a contact by id (full ContactPatchBody; prefer doPatchContactName for name-only).
     * @param id – contact UUID
     * @param body – partial contact fields
     * @returns Promise with { data, error, pending, refresh }
     */
    const doUpdateContact = async (id: string, body: ContactPatchBody) => {
        const result = await useApiFetch<Contact>(`/api/contacts/${id}`, {
            method: 'PATCH',
            body,
        })
        return result
    }

    /**
     * Delete a contact by id.
     * @param id – contact UUID
     * @returns Promise with { data, error, pending, refresh } (data is undefined on success)
     */
    const doDeleteContact = async (id: string) => {
        const result = await useApiFetch(`/api/contacts/${id}`, {
            method: 'DELETE',
        })
        return result
    }

    /**
     * Archive (soft-delete) a contact by id. Hidden from list/detail afterward; notes already sent
     * stay on record. Prefer this over a hard delete (no DELETE route exists for contacts).
     * @param id – contact UUID
     */
    const doArchiveContact = async (id: string) => {
        const result = await useApiFetch(`/api/contacts/${id}/archive`, {
            method: 'PATCH',
        })
        return result
    }

    /**
     * Fetch handwriting projects with a note addressed to this contact (for the contact detail
     * page's "Recent Notes" list). Same wire shape as `/api/handwriting-projects/recent`.
     * @param id – contact UUID
     */
    const doFetchContactHandwritingProjects = async (id: string) => {
        const result = await useApiFetch<{ projects: RecentHandwritingProject[] }>(
            `/api/contacts/${id}/handwriting-projects`,
            { method: 'GET' }
        )
        return result
    }

    /**
     * Add an address to a contact (separate POST; edit drawer uses PATCH one address at a time).
     * @param contactId – contact UUID
     * @param body – address payload + optional is_default
     */
    const doAddAddress = async (
        contactId: string,
        body: AddressPayload & { is_default?: boolean }
    ) => {
        const result = await useApiFetch<{ address: unknown }>(
            `/api/contacts/${contactId}/addresses`,
            { method: 'POST', body }
        )
        return result
    }

    /**
     * Update one address row (primary path for contact edit drawer: single-address submit).
     * @param contactId – contact UUID
     * @param addressId – address UUID
     * @param body – partial address fields + optional is_default
     */
    const doPatchAddress = async (
        contactId: string,
        addressId: string,
        body: Partial<AddressPayload> & { is_default?: boolean }
    ) => {
        const result = await useApiFetch<{ address: unknown }>(
            `/api/contacts/${contactId}/addresses/${addressId}`,
            { method: 'PATCH', body }
        )
        return result
    }

    /**
     * Delete an address (for edit form). Fails with 400 if contact would have zero addresses.
     * @param contactId – contact UUID
     * @param addressId – address UUID
     */
    const doDeleteAddress = async (contactId: string, addressId: string) => {
        const result = await useApiFetch(`/api/contacts/${contactId}/addresses/${addressId}`, {
            method: 'DELETE',
        })
        return result
    }

    return {
        doFetchContactList,
        doCreateContact,
        doCreateContactWithAddress,
        doFetchContactById,
        doPatchContactName,
        doUpdateContact,
        doDeleteContact,
        doArchiveContact,
        doFetchContactHandwritingProjects,
        doAddAddress,
        doPatchAddress,
        doDeleteAddress,
    }
}
